<template>
    <div class="modal fade" :id="id" aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
        <div class="modal-dialog modal-dialog-centered modal-dialog-scv" role="document">
            <div class="modal-content modal-content-scv" style="position: relative; width: 840px">

                <div class="modal-header modal-header-scv-cmn">
                    <div class="modal-header-text-box-scv" style="width: 100%;">
                        <span class="modal-header-text-scv">
                            <template v-if="props.addMode">
                                パッケージ名を指定して追加
                            </template>
                            <template v-else>
                                編集
                            </template>
                        </span>
                    </div>
                    <div class="btn-scv-close">
                        <a class="btn-scv-close" href="#" role="button" @click="onCancel">
                            <div class="icon-font-slategray">
                                <i class="icon-delete"></i>
                            </div>
                        </a>
                    </div>
                </div>

                <div class="modal-body modal-body-scv">
                    <div class="infomaiton">
                        <template v-if="!untrustedAppsPolicy">
                            <span>
                                利用を許可するアプリケーションを登録してください。
                            </span>
                            <span class="scv-annotation-text" style="font-size: 14px;">
                                ※提供元が不明なアプリケーション（Google Playストア以外のアプリケーション）を登録することができます。
                            </span>
                        </template>
                        <template v-else>
                            <span>
                                利用を許可するアプリケーションを登録してください。
                            </span>
                            <div class="attention">
                                <div class="d-flex flex-row attention-inner">
                                    <div class="icon-font-alert-s">
                                        <i class="icon-alert" />
                                    </div>
                                    <span class="alert-text attention-text">
                                        「アプリケーションのインストール設定 - 提供元が不明なアプリケーションのインストールを禁止する」が有効のため、<br />
                                        提供元が不明なアプリケーションは登録してもインストールできません。
                                    </span>
                                </div>
                            </div>
                        </template>
                    </div>

                    <div class="target-application">
                        <div class="main-category">
                            <span>対象アプリケーション</span>
                        </div>
                        <div class="setting-area">
                            <div class="d-flex flex-row input-area">
                                <span class="category">アプリケーション名：</span>
                                <div class="d-flex flex-column">
                                    <MaskInput convertType="text" class="input-text-scv" v-model="applicationName"
                                        :maxlength=AndroidAppDistDefinition.APPLICATION_NAME_MAX_LENGTH
                                        placeholder="<空の場合は、パッケージ名を表示します>" style="width: 636px" />
                                    <div class="modal-body-input-error-area-scv">
                                        <span class="scv-form-validation-error error">{{ errorApplicationName }}</span>
                                    </div>
                                </div>
                            </div>
                            <div class="d-flex flex-row input-area">
                                <span class="category">パッケージ名：</span>
                                <div class="d-flex flex-column">
                                    <MaskInput convertType="package-name" class="input-text-scv"
                                        :class="{ 'input-validation-error': errorPackageName != '' }"
                                        v-model="packageName" v-bind:disabled="!props.addMode"
                                        :maxlength=AndroidAppDistDefinition.PACKAGE_NAME_MAX_LENGTH placeholder="<入力必須>"
                                        style="width: 636px" />
                                    <div class="modal-body-input-error-area-scv">
                                        <span class="scv-form-validation-error error">{{ errorPackageName }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="setting-application">
                        <div class="main-category">
                            <span>アプリケーション設定</span>
                        </div>
                        <div class="setting-box">
                            <label class="scv-check-label-box">
                                <input type="checkbox" v-model="applicationDisabled">
                                <span>一時的に利用を停止する</span>
                            </label>
                            <InformationManual
                                :arMessage="['<div>アプリケーションの起動ができなくなります。<br />利用を停止しても、アプリケーションのデータは端末から削除されません。</div>']"
                                style="margin-left: 4px;" />
                        </div>
                    </div>
                </div>

                <div class="modal-footer modal-footer-scv">
                    <div class="d-flex justify-content-center">
                        <button type="button" class="btn-scv-blue" id="buttonApply" @click="onOk"
                            style="margin-right: 40px;">OK</button>
                        <button type="button" class="btn-scv-white" @click="onCancel">キャンセル</button>
                    </div>
                </div>

            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
// ClassName    AppMgrPermissionAppPackageNameModal

import { ref, Ref, computed, onMounted } from "vue";
import MaskInput from '@/components/Input/MaskInput.vue';
import { MessageText } from '@/models/Message/MessageText';
import { AndroidAppMgrApplicationSetting } from '@/models/Mobile/AndroidAppDist/AndroidAppMgrApplicationSetting'
import { AndroidAppDistDefinition } from "@/components/Mobile/AndroidAppDist/AndroidAppDistDefinition";
import '@/util/Extension/StringExtensions'
import InformationManual from '@/components/InformationManual.vue';
import { ApplicationPolicyConverter } from '@/models/Mobile/AndroidAppDist/ApplicationPolicyConverter'
import { BootstrapUtil } from "lib-vue-v2/util";

///////////////////////////////////////////////////////
/// Props
///////////////////////////////////////////////////////
interface Props {
    getSelectTopAppData: (() => AndroidAppMgrApplicationSetting | undefined);
    subId: string;                       // SubId
    addMode: boolean;                   // 追加編集
    untrustedAppsPolicy: boolean;       // 提供元が不明なアプリケーションのインストールを禁止する
    mdmAppPackageName: string;          // SKYSEA MDMアプリのパッケージ名
    blackPackageList: Set<string>;      // 現在登録済みのアプリケーション<ブラックリスト>
    whitePackageList: Set<string>;      // 現在登録済みのアプリケーション<ホワイトリスト>
}

const props = withDefaults(defineProps<Props>(), {
});

///////////////////////////////////////////////////////
/// Emits
///////////////////////////////////////////////////////
interface Emits {
    (event: "apply", applicationName: string, packageName: string, applicationDisabled: boolean, addMode: boolean): void;
}
const emit = defineEmits<Emits>();

///////////////////////////////////////////////////////
/// Data
///////////////////////////////////////////////////////
const id = computed(() => "AppMgrPermissionAppPackageNameModal" + props.subId);
const applicationName: Ref<string> = ref("");           // アプリケーション名
const packageName: Ref<string> = ref("");               // パッケージ名
const applicationDisabled: Ref<boolean> = ref(false);    // 一時的に利用を停止する
const errorPackageName: Ref<string> = ref("");          // パッケージ名のエラー内容
const errorApplicationName: Ref<string> = ref("");          // パッケージ名のエラー内容

///////////////////////////////////////////////////////
/// Function
///////////////////////////////////////////////////////
onMounted(() => {
    const el = document.getElementById(id.value);
    if(null != el) {
        // 画面が開いた時の処理
        el.addEventListener('show.bs.modal', () => {
            if (props.addMode) {
                init();
            }
            else {
                const selectTopAppData = props.getSelectTopAppData();
                if (selectTopAppData != undefined) {
                    const converter = new ApplicationPolicyConverter();
                    applicationName.value = selectTopAppData.basicInfo.appName;
                    packageName.value = selectTopAppData.basicInfo.packageName;
                    applicationDisabled.value = converter.GetDisabled(selectTopAppData.policyJson);
                }
                errorPackageName.value = "";
                errorApplicationName.value = "";
            }
        });

        // 画面を閉じた際の処理
        el.addEventListener('hidden.bs.modal', () => {
            init();
        });
    }
});

// 画面初期化
const init = ((): void => {
    applicationName.value = "";
    packageName.value = "";
    applicationDisabled.value = false;
    errorPackageName.value = "";
    errorApplicationName.value = "";
});

// 項目チェック
const validateCheckPackageName = ((): boolean => {
    errorPackageName.value = ""
    packageName.value = packageName.value.trim();

    // 空
    if (!packageName.value) {
        errorPackageName.value = MessageText.CommonInputFailedEmpty;
        return false;
    }

    // length
    if (packageName.value.length < AndroidAppDistDefinition.PACKAGE_NAME_MIN_LENGTH || AndroidAppDistDefinition.PACKAGE_NAME_MAX_LENGTH < packageName.value.length) {
        errorPackageName.value = MessageText.CommonInputFailedRength.format(AndroidAppDistDefinition.PACKAGE_NAME_MIN_LENGTH.toString(), AndroidAppDistDefinition.PACKAGE_NAME_MAX_LENGTH.toString());
        return false
    }

    // 制御文字
    if (packageName.value.includeControlCode()) {
        errorPackageName.value = MessageText.CommonInputFailedControlCode;
        return false;
    }

    // mdmAppPackageName
    if (packageName.value.toLowerCase() === props.mdmAppPackageName) {
        errorPackageName.value = "「SKYSEA MDM」アプリは[追加]ボタンの「「SKYSEA MDM」アプリを追加」から登録してください。";
        return false;
    }

    // packageName format
    if (!packageName.value.isValidPackageName()) {
        errorPackageName.value = "パッケージ名の形式ではありません。";
        return false;
    }

    // 重複チェック
    if (props.addMode) {
        if (props.blackPackageList.has(packageName.value.toLowerCase())) {
            errorPackageName.value = "すでに同じアプリケーションが登録されています。";
            return false;
        }
        if (props.whitePackageList.has(packageName.value.toLowerCase())) {
            errorPackageName.value = "すでに同じアプリケーションが登録されています。";
            return false;
        }
    }
    return true;
});

const validateCheckApplicationName = ((): boolean => {
    errorApplicationName.value = ""
    applicationName.value = applicationName.value.trim();

    // length
    if (AndroidAppDistDefinition.APPLICATION_NAME_MAX_LENGTH < applicationName.value.length) {
        errorApplicationName.value = MessageText.CommonInputFailedRength.format(AndroidAppDistDefinition.APPLICATION_NAME_MIN_LENGTH.toString(), AndroidAppDistDefinition.APPLICATION_NAME_MAX_LENGTH.toString());
        return false
    }

    // 制御文字
    if (applicationName.value.includeControlCode()) {
        errorApplicationName.value = MessageText.CommonInputFailedControlCode;
        return false;
    }
    return true;
});

// OK押下
const onOk = ((): void => {
    let validateCheckPackageNameResult = validateCheckPackageName();
    let validateCheckApplicationNameResult = validateCheckApplicationName();
    if (!validateCheckPackageNameResult || !validateCheckApplicationNameResult) {
        return;
    }
    if (applicationName.value) {
        emit("apply", applicationName.value, packageName.value, applicationDisabled.value, props.addMode);
    }
    else {
        emit("apply", packageName.value, packageName.value, applicationDisabled.value, props.addMode);
    }
    hide();
});

// Close押下
const onCancel = ((): void => {
    hide();
});

// CloseModal
const hide = ((): void => {
    BootstrapUtil.hideModal(id.value);
});
</script>

<style scoped>
.infomaiton span {
    display: block;
    height: 20px;
}

.infomaiton span:not(:last-child) {
    margin-bottom: 4px;
}

.attention {
    height: 56px;
    padding: 8px;
    margin-top: 16px;
    margin-right: 24px;
    border-radius: 4px;
    border: 1px solid #1464B933;
}

.attention .attention-inner {
    height: 20px;
}

.attention .attention-inner .attention-text {
    margin-left: 8px;
    font-size: 13px;
    color: #F07823;
}


.target-application {
    margin-top: 16px;
}

.setting-application {
    margin-top: 16px;
}

.main-category {
    height: 44px;
    padding-top: 10px;
    padding-bottom: 10px;
    color: #2582D7;
    border-bottom: 1px solid #BEE6FF;
    margin-bottom: 16px;
}

.setting-box {
    display: flex;
    align-items: center;
    flex-direction: row;
}

.setting-area {
    padding-left: 16px;
    padding-right: 16px;
}

.input-area {
    height: 48px;
}

.input-area .category {
    display: flex;
    align-items: center;
    width: 140px;
    height: 32px;
}
</style>